import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeTable, Column } from '@/components/ui/tee-table';
import { TeeActionsMenu } from '@/components/ui/tee-actions-menu';
import { TeeConfirmationDialog } from '@/components/ui/tee-confirmation-dialog';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeButton } from '@/components/ui/tee-button';
import { usePlaylists } from '@/hooks/usePlaylists';
import { Playlist } from '@/types/playlist';
import { ContentStatus } from '@/types/contentItem';
import { Pagination } from '@/types/pagination';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import PlaylistFormModal from './PlaylistFormModal';

interface PlaylistsPanelProps {
  createModalOpen: boolean;
  onCloseCreateModal: () => void;
}

export default function PlaylistsPanel({ createModalOpen, onCloseCreateModal }: PlaylistsPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    playlists,
    loading,
    error,
    total,
    offset,
    setOffset,
    limit,
    listPlaylists,
    deletePlaylist,
    togglePublish,
  } = usePlaylists();

  const [deleteTarget, setDeleteTarget] = useState<Playlist | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // The empty-state CTA also opens the create modal, but the parent
  // (ContentStudioPage, Plan 08) only owns the section-header CTA's open
  // state via `createModalOpen`. Track a second local trigger so the
  // empty-state button can open the same modal without the panel taking
  // ownership of the parent's CTA state.
  const [emptyStateModalOpen, setEmptyStateModalOpen] = useState(false);

  useEffect(() => {
    listPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pagination: Pagination = { page: currentPage, pageSize: limit, total, totalPages };

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
    listPlaylists({ offset: newOffset });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deletePlaylist(deleteTarget.id);
      showSuccessToast(t('contentLibrary.playlistDeleted'));
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch {
      showErrorToast(t('contentLibrary.errorDeletePlaylist'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTogglePublish = async (row: Playlist) => {
    try {
      await togglePublish(row.id, row.status);
      showSuccessToast(
        t(
          row.status === ContentStatus.PUBLISHED
            ? 'contentLibrary.playlistUnpublished'
            : 'contentLibrary.playlistPublished'
        )
      );
    } catch {
      showErrorToast(t('contentLibrary.errorStatusUpdate'));
    }
  };

  const handleCloseCreateModal = () => {
    onCloseCreateModal();
    setEmptyStateModalOpen(false);
  };

  const columns: Column<Playlist>[] = [
    {
      key: 'name',
      label: t('contentLibrary.colName'),
      render: (_v, row) => row.name,
    },
    {
      key: 'itemCount',
      label: t('contentLibrary.colItemCount'),
      render: (_v, row) => row.itemCount,
    },
    {
      key: 'status',
      label: t('contentLibrary.colStatus'),
      render: (_v, row) =>
        row.status === ContentStatus.PUBLISHED ? (
          <TeeBadge type="green" label={t('contentLibrary.statusPublished')} />
        ) : (
          <TeeBadge type="grey" label={t('contentLibrary.statusDraft')} />
        ),
    },
    {
      key: 'updatedAt',
      label: t('contentLibrary.colUpdated'),
      render: (_v, row) => new Date(row.updatedAt).toLocaleDateString(),
    },
    {
      key: 'id',
      label: '',
      render: (_v, row) => (
        <TeeActionsMenu
          onEdit={() => navigate(`/content-library/studio/playlists/${row.id}`)}
          onDelete={() => {
            setDeleteTarget(row);
            setDeleteConfirmOpen(true);
          }}
          onToggleStatus={() => handleTogglePublish(row)}
          status={row.status === ContentStatus.PUBLISHED ? 'active' : 'inactive'}
        />
      ),
    },
  ];

  let content: ReactNode;
  if (loading) {
    content = <TeeLoader />;
  } else if (error) {
    content = (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-body-m text-[var(--text-content-sub-title)]">
          {t('contentLibrary.errorLoadPlaylists')}
        </p>
        <TeeButton variant="secondary" onClick={() => listPlaylists()}>
          {t('contentLibrary.retry')}
        </TeeButton>
      </div>
    );
  } else if (playlists.length === 0) {
    content = (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <p className="text-heading-h4 font-semibold text-[var(--text-content-title)]">
          {t('contentLibrary.emptyPlaylistsHeading')}
        </p>
        <p className="text-body-m text-[var(--text-content-sub-title)]">
          {t('contentLibrary.emptyPlaylistsBody')}
        </p>
        <TeeButton variant="primary" onClick={() => setEmptyStateModalOpen(true)}>
          {t('contentLibrary.createPlaylist')}
        </TeeButton>
      </div>
    );
  } else {
    content = (
      <TeeTable
        columns={columns}
        data={playlists}
        pagination={total > 0 ? pagination : undefined}
        onPageChange={handlePageChange}
        rowKey={(row) => row.id}
      />
    );
  }

  return (
    <div className="section_body">
      {content}

      <PlaylistFormModal
        isOpen={createModalOpen || emptyStateModalOpen}
        editTarget={null}
        onClose={handleCloseCreateModal}
        onSaved={() => listPlaylists()}
      />

      <TeeConfirmationDialog
        isOpen={deleteConfirmOpen}
        variant="destructive"
        title={t('contentLibrary.deletePlaylistTitle')}
        description={t('contentLibrary.deletePlaylistBody', { name: deleteTarget?.name })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
}
